import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios from 'axios';
import produce from 'immer';
import {
  DEFAULT_PAGINATION,
  DEVICE_VIEWER,
  DEVICE_VIEWER_ASPECT,
  FILE_NAME_TYPE,
} from '../../commons/constants/constants';
import { uploadFiles } from '../../services/uploader.service';
import { PageRequest, PaginationType } from '../../types/CommonTypes';
import { TourGuideData } from '../../types/TourSoundData';
import { storeName as entityStore } from '../entities/tour.entity';
import { RootModel } from '../models';
import { storeName as userStore } from './user.model';
import { changeAspect } from '../../utils/KrPanoUtils';
import Tour from '../../model/Tour';
import { updateConfigLanguages } from '../../services/tour.service';

export const storeName = 'tours';

export interface initialValuesType {
  ids: (number | string)[];
  pagination: PaginationType | null;
  criteria: { [key: string]: any };
  visible: boolean;
  loading: boolean;
  editId: number;
  cloningId: number;

  tourGuideModalVisible: boolean;
  editTourGuide: TourGuideData;
  visibleHotspotList: boolean;
  displayGridLine: boolean;
  deviceViewer: string;
  tourLanguageModalVisible: boolean;
}

const initialState: initialValuesType = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  criteria: {},
  visible: false,
  loading: false,
  editId: null,
  cloningId: null,

  editTourGuide: undefined,
  tourGuideModalVisible: false,

  visibleHotspotList: false,
  displayGridLine: false,
  deviceViewer: DEVICE_VIEWER.desktop,
  tourLanguageModalVisible: false,
};

export const tours: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setCloningId: produce((state, payload) => {
      state.cloningId = payload;
    }),

    setEditTourGuide: produce((state, payload) => {
      state.editTourGuide = payload;
      state.tourGuideModalVisible = !!payload;
    }),
    setTourGuideModalVisible: produce((state, payload) => {
      state.tourGuideModalVisible = payload;
    }),
    setVisibleHotspotList: produce((state, payload) => {
      state.visibleHotspotList = payload;
    }),
    setDisplayGridLine: produce((state, payload) => {
      state.displayGridLine = payload;
    }),
    setDeviceView: produce((state, payload) => {
      state.deviceViewer = payload;
    }),
    setTourLanguageModalVisible: produce((state, payload) => {
      state.tourLanguageModalVisible = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof tours>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await this.filterAsync({ ...pagination, page: 0 });
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async filterAsync(params = {}, state) {
      this.setLoading(true);
      try {
        const data = await dispatch[entityStore].load(params);
        if (data && data.content) {
          this.setIds(data.content.map((tour) => tour.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
          this.setCriteria(params);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to filter tours');
      } finally {
        this.setLoading(false);
      }
    },
    async saveAsync(payload, state) {
      this.setLoading(true);
      let item;
      try {
        await dispatch[entityStore].save(payload);

        if (!payload.id) {
          this.resetFilter();
        } else {
          this.reloadList();
        }

        this.setVisible(false);
        message.success('Success to save tour');
      } catch (e) {
        console.error(e);
        message.error('Fail to save tour');
      } finally {
        this.setLoading(false);
      }
      return item;
    },

    async delete(id) {
      this.setLoading(true);
      try {
        await dispatch[entityStore].delete(id);
        await this.reloadList();
        message.success('Success to delete tour');
      } catch (e) {
        console.error(e);
        message.error('Fail to delete tour');
      } finally {
        this.setLoading(false);
      }
    },
    async makeTour(payload, state) {
      this.setLoading(true);
      const { user } = state[userStore];
      try {
        const params = { ...payload };
        const uploadActions = [];
        const { images: imageFiles } = params;
        if (imageFiles && !!imageFiles.length) {
          uploadActions.push(
            uploadFiles(params.id, imageFiles, FILE_NAME_TYPE.PANO, user.id, 'scenes', true, true)
          );
        }
        const files = await axios.all(uploadActions);
        delete params.images;
        files.forEach((file) => {
          const { filesList, name } = file;
          if (!(name && filesList.length)) {
            return;
          }
          params.images = filesList.map((f: any) => {
            return { id: f.id, url: f.url };
          });
        });
        await dispatch[entityStore].make(params);
        message.success('Success to make scene');

        this.resetFilter();
      } catch (err) {
        message.error(`Fail to save make : ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async cloneTour(payload) {
      this.setLoading(true);
      const { id, name } = payload;
      try {
        message.info(`Cloning tour ${name}`);
        this.setCloningId(id);
        await dispatch[entityStore].clone(id);
        message.success(`Success to clone tour ${name}`);
        this.resetFilter();
      } catch (e) {
        console.error(e);
        message.error(`Fail to clone tour ${name}`);
      } finally {
        this.setLoading(false);
        this.setCloningId(null);
      }
    },
    toggleAspect(tour: Tour, state) {
      changeAspect(tour, DEVICE_VIEWER_ASPECT[state[storeName].deviceViewer]);
    },
    async updateConfigLanguages(payload, state) {
      this.setLoading(true);
      try {
        await updateConfigLanguages(payload);
        message.success(`Update config language successfully`);
        this.resetFilter();
      } catch (err) {
        message.error(`Fail to update config language: ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default tours;
